import React, { createContext, useContext, useEffect, useState } from "react"
import { API_ROUTES } from "../configuration/RoutesApi"
import { GetRequest } from "../services/ApiClientService"
import { CourierEntity } from "../Objects/Courier/CourierEntity"
import { ECourierView } from "../Enum/ECourierView"
import { ERequestType } from "../Enum/ERequestType"
import { isTokenValid } from "../Objects/Generics/Helps"

export interface CourierAuthState {
  token: string | null
  courier: CourierEntity | null
  status: boolean
}

interface useCourierViewContextData {
  currentView: ECourierView
  setCurrentView: (view: ECourierView) => void
  updateCourier: (courier: CourierEntity) => void
  auth: CourierAuthState
  login: (token: string) => void
  logout: () => void
  reloadCourier: () => Promise<void>
  isAuthenticated: boolean
}

const CourierViewContext = createContext<useCourierViewContextData | undefined>(undefined)

export const CourierViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentView, setCurrentView] = useState<ECourierView>(ECourierView.RegisterView)

  const [auth, setAuth] = useState<CourierAuthState>({
    token: null,
    courier: null,
    status: false,
  })

  useEffect(() => {
    const token = localStorage.getItem(API_ROUTES.TOKEN_COURIER)

    if (!token) {
      return
    }

    if (!isTokenValid(token)) {
      logout()

      return
    }

    setAuth({
      token,
      courier: null,
      status: true,
    })

    load()
  }, [])

  const load = async () => {
    try {
      const response = await GetRequest<CourierEntity>(API_ROUTES.DELIVERY.GET_COURIER_UPDATED, ERequestType.Courier)

      if (!response.success) {
        return
      }

      setAuth((prev) => ({
        ...prev,
        courier: new CourierEntity(response.data),
        status: true,
      }))
    } catch (error) {
      logout()
    }
  }

  const login = (token: string) => {
    localStorage.setItem(API_ROUTES.TOKEN_COURIER, token)

    setAuth({
      token,
      courier: null,
      status: true,
    })

    load()
  }

  const logout = () => {
    localStorage.removeItem(API_ROUTES.TOKEN_COURIER)

    setAuth({
      token: null,
      courier: null,
      status: false,
    })

    setCurrentView(ECourierView.RegisterView)
  }

  const updateCourier = (courier: CourierEntity) => {
    setAuth((prev) => ({
      ...prev,
      courier: new CourierEntity(courier),
    }))
  }

  const reloadCourier = async () => {
    if (!auth.token) {
      return
    }

    await load()
  }

  return (
    <CourierViewContext.Provider
      value={{
        auth,
        currentView,
        setCurrentView,
        updateCourier,
        login,
        logout,
        reloadCourier,
        isAuthenticated: !!auth.token,
      }}
    >
      {children}
    </CourierViewContext.Provider>
  )
}

export const useCourierViewContext = (): useCourierViewContextData => {
  const context = useContext(CourierViewContext)

  if (!context) {
    throw new Error("useCourierViewContext deve ser usado dentro de provider")
  }

  return context
}
